'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { useWebOrdersQuery } from '@/app/[locale]/doi-tra/_hooks/react-query/use-web-orders'
import { toast } from '@/components/common/toast'
import { useIsLoggedIn, useUserQuery } from '@/hooks/react-query/user/use-user'
import { cn } from '@/lib/utils'
import { __cExchangeService } from '@/services/client/__c.exchange.service'
import { EPlatformType } from '@/types/exchange-order'
import {
  VERIFY_CARD_CLASS,
  VerifyFormActions,
  VerifyInputField,
  VerifyPageShell,
} from '../_components/verify-form'
import { OrderCodeGuideCard } from './components/order-code-guide-card'
import { PhoneConfirmDialog } from './components/phone-confirm-dialog'
import { GuidePlatform } from './components/platform-guide.config'
import { RecentOrderList } from './components/recent-order-list'

const verifySchema = z.object({
  orderCode: z
    .string()
    .min(1, 'Mã đơn hàng không được để trống')
    .regex(/^[A-Z0-9]+$/, 'Mã đơn hàng không hợp lệ'),
})

type VerifyFormData = z.infer<typeof verifySchema>

export default function VerifyOrderPage() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [isVerifying, setIsVerifying] = useState(false)
  const [showPhoneConfirm, setShowPhoneConfirm] = useState(false)
  const [pendingOrderCode, setPendingOrderCode] = useState<string | null>(null)
  const [pendingPlatformType, setPendingPlatformType] =
    useState<EPlatformType | null>(null)

  const [guidePlatform, setGuidePlatform] = useState<GuidePlatform>('web')

  const isLoggedIn = useIsLoggedIn()
  const { data: userData } = useUserQuery()
  const { data: webOrdersResponse } = useWebOrdersQuery()

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
  } = useForm<VerifyFormData>({
    resolver: zodResolver(verifySchema),
    defaultValues: {
      orderCode: '',
    },
  })

  useEffect(() => {
    const initialOrderCode = searchParams.get('orderCode')
    if (initialOrderCode) {
      setValue('orderCode', initialOrderCode.toUpperCase())
    }
  }, [searchParams, setValue])

  // Helper function để kiểm tra xem mã đơn hàng có thuộc danh sách đơn hàng web hay không
  const isWebOrder = (orderCode: string): boolean => {
    if (!webOrdersResponse?.data?.orders) return false

    return webOrdersResponse.data.orders.some(
      (order) =>
        order.order_code === orderCode && order.order_status === 'completed'
    )
  }

  // Helper function để lấy tên platform
  const getPlatformDisplayName = (platformType: EPlatformType): string => {
    const platformDisplayName: Record<EPlatformType, string> = {
      [EPlatformType.WEB]: 'Website',
      [EPlatformType.SHOPEE]: 'Shopee',
      [EPlatformType.TIKTOK]: 'TikTok',
      [EPlatformType.LAZADA]: 'Lazada',
    }
    return platformDisplayName[platformType]
  }

  // Helper function để navigate trực tiếp đến trang exchange cho đơn hàng web
  const navigateToWebExchange = async (orderCode: string) => {
    try {
      const response =
        await __cExchangeService.checkGetAuthorizationCodeWebOrder(orderCode)

      if (response.state && response.data.authorization_code) {
        if (userData?.phone) {
          // User có phone - chuyển thẳng đến trang exchange
          const targetUrl = `/doi-tra/web/exchange?orderCode=${orderCode}&authorizationCode=${response.data.authorization_code}&phone=${userData.phone}`

          toast.message({
            title: 'Xác thực thành công!',
            message: 'Đang chuyển đến trang đổi hàng...',
          })

          router.push(targetUrl)
        } else {
          // User chưa có phone - chuyển đến verify-phone
          const targetUrl = `/doi-tra/web/verify-phone?orderCode=${orderCode}&authorizationCode=${response.data.authorization_code}`

          toast.message({
            title: 'Xác thực thành công!',
            message:
              'Tài khoản chưa có số điện thoại. Đang chuyển đến bước xác thực số điện thoại...',
          })

          router.push(targetUrl)
        }
      } else {
        throw new Error(response.message || 'Không thể xác thực đơn hàng')
      }
    } catch (error) {
      console.error('❌ Web order verification failed:', error)
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Có lỗi xảy ra, vui lòng thử lại.'
      setError('orderCode', { message: errorMessage })
    }
  }

  // Helper function để navigate đến verify-phone page
  const navigateToVerifyPhone = (
    platformType: EPlatformType,
    orderCode: string,
    phone?: string
  ) => {
    const nextStepPath = `/doi-tra/${platformType}/verify-phone`
    const targetUrl = phone
      ? `${nextStepPath}?orderCode=${orderCode}&phone=${phone}`
      : `${nextStepPath}?orderCode=${orderCode}`

    const platformName = getPlatformDisplayName(platformType)
    const title = phone ? 'Xác nhận thành công!' : 'Đã tìm thấy đơn hàng!'
    const message = `Đang chuyển đến bước xác thực cho ${platformName}...`

    toast.message({ title, message })
    router.push(targetUrl)
  }

  // Helper function để reset dialog state
  const resetDialogState = () => {
    setShowPhoneConfirm(false)
    setPendingOrderCode(null)
    setPendingPlatformType(null)
  }

  const handleVerifyOrder = async (data: VerifyFormData) => {
    setIsVerifying(true)
    const { orderCode } = data

    try {
      // Kiểm tra xem mã đơn hàng có thuộc danh sách đơn hàng web hay không
      if (isWebOrder(orderCode)) {
        // Nếu là đơn hàng web, chuyển thẳng đến trang exchange (bỏ qua verify OTP)
        await navigateToWebExchange(orderCode)
        return
      }

      // Nếu không phải đơn hàng web, kiểm tra platform type như cũ
      const result = await __cExchangeService.checkTypeOrder(orderCode)

      if (!result.data.type) {
        throw new Error(
          result.message || 'Mã đơn hàng không hợp lệ hoặc không được hỗ trợ.'
        )
      }

      const platformType = result.data.type as EPlatformType

      // Nếu là Web platform, chuyển đến verify-phone như cũ
      if (platformType === EPlatformType.WEB) {
        navigateToVerifyPhone(platformType, orderCode)
        return
      }

      // Nếu là platform khác Web và user đã đăng nhập, hiển thị confirm dialog
      if (userData?.phone) {
        setPendingOrderCode(orderCode)
        setPendingPlatformType(platformType)
        setShowPhoneConfirm(true)
        return
      }

      // Nếu user chưa đăng nhập, chuyển đến verify-phone như cũ
      navigateToVerifyPhone(platformType, orderCode)
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Có lỗi xảy ra, vui lòng thử lại.'
      console.error('❌ Verification failed:', error)
      setError('orderCode', { message: errorMessage })
    } finally {
      setIsVerifying(false)
    }
  }

  const handleConfirmPhone = () => {
    if (!pendingOrderCode || !pendingPlatformType || !userData?.phone) return

    navigateToVerifyPhone(pendingPlatformType, pendingOrderCode, userData.phone)
    resetDialogState()
  }

  const handleUseDifferentPhone = () => {
    if (!pendingOrderCode || !pendingPlatformType) return

    navigateToVerifyPhone(pendingPlatformType, pendingOrderCode)
    resetDialogState()
  }

  const isLoading = isVerifying

  return (
    <>
      <VerifyPageShell>
        <OrderCodeGuideCard
          platform={guidePlatform}
          onPlatformChange={setGuidePlatform}
          isLoggedIn={isLoggedIn}
          className={VERIFY_CARD_CLASS}
        />

        <form
          onSubmit={handleSubmit(handleVerifyOrder)}
          className={cn(VERIFY_CARD_CLASS, 'flex flex-col gap-4 md:gap-8')}
        >
          <VerifyInputField
            id='orderCode'
            label='Mã đơn hàng'
            {...register('orderCode', {
              onChange: (e) => (e.target.value = e.target.value.toUpperCase()),
            })}
            placeholder='Vui lòng nhập mã đơn hàng'
            disabled={isLoading}
            errorMessage={errors.orderCode?.message}
          />
          <VerifyFormActions loading={isLoading} disabled={isLoading} />
        </form>

        {guidePlatform === 'web' && isLoggedIn && (
          <RecentOrderList className='md:rounded-3xl' />
        )}
      </VerifyPageShell>

      <PhoneConfirmDialog
        isOpen={showPhoneConfirm}
        onCloseAction={() => {
          setShowPhoneConfirm(false)
          setPendingOrderCode(null)
          setPendingPlatformType(null)
        }}
        phoneNumber={userData?.phone || ''}
        onConfirmAction={handleConfirmPhone}
        onUseDifferentPhoneAction={handleUseDifferentPhone}
        isLoading={isLoading}
      />
    </>
  )
}
